
const id = <T>(v: T): T => v;

type ID = number|string;
type Uniq = {id: ID};

export const getId = <T extends {id: any}>({id}: T) =>
    id;
export const byId = <T extends Uniq>(refId: ID) =>
    ({id}: T) => id == refId;
export const idsMap = <T extends Uniq, T1 = T>(
    items: T[],
    fn: (item: T) => T1 = id as (i: T) => T1
): {[key in ID]: T1} =>
        Object.fromEntries(items.map(item => [item.id, fn(item)]));

export const keyMap = <T extends object, T1 = T>(
    items: T[],
    key: keyof T | ((item: T) => string),
    fn: (item: T) => T1 = id as (i: T) => T1
): {[key in ID]: T1} =>
        Object.fromEntries(
            items.map(item => [ 
                typeof key == "function" ? key(item) : item[key], 
                fn(item)
            ]));

export const mapDict = <T, T1>(
    obj: {[key in ID]: T},
    fn: (val: T) => T1
): {[key: keyof typeof obj]: T1} =>
        Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, fn(v)]));

export const mapDictWithKey = <T extends object, T1, K1 extends ID = ID>(
    obj: {[key in ID]: T},
    fn: (k: K1, val: T) => any
): {[key in K1]: T1} =>
        Object.fromEntries(Object.entries(obj)
            .map(([k, v]) => [k, fn(k as K1, v)])) as Record<K1, T1>;

export const filterDict = <T>(
    object: {[key in ID]: T},
    filter: (el: T) => boolean
): {[key in ID]: T} =>
        Object.fromEntries(Object.entries(object)
            .filter(([k, v]) => filter(v)));

export const keysWithTrueVals = <T, T1>(
    object: {[key in ID]: T},
    filter: (el: T) => T1 = id as (i: T) => T1
): ID[] =>
        Object.entries(object)
            .filter(([, v]) => filter(v))
            .map(([k]) => k);

export const arrToSet = (arr: ID[]) =>
    Object.fromEntries(arr.map(id => [id, true]));

export const findOrFirst = <T>(
    arr: T[],
    cond: (el: T) => boolean
): T => arr.find(cond) ?? arr[0];

export const isEmpty = (obj: object) => Object.keys(obj).length == 0;

export const applyIfFunc = <
    T extends (number | string | boolean | object | any[]),
    Args extends any[]
>(
        v: T | ((...args: Args) => T),
        ...args: Args
    ): T =>
        typeof v === "function" ? v(...args) : v;

function getCircularReplacer() {
    const ancestors = [];
    return function (key, value) {
        if (typeof value !== "object" || value === null) {
        return value;
        }
        // `this` is the object that value is contained in,
        // i.e., its direct parent.
        while (ancestors.length > 0 && ancestors.at(-1) !== this) {
        ancestors.pop();
        }
        if (ancestors.includes(value)) {
        return "[Circular]";
        }
        ancestors.push(value);
        return value;
    };
}

export const objEqual = (a: object, b: object) =>
    JSON.stringify(a, getCircularReplacer()) === JSON.stringify(b, getCircularReplacer());

export const sum = (vals: number[]) => vals.reduce((s, v) => s + v, 0);
export const sumDict = (dict: {[k in any]: number}) => sum(Object.values(dict));


export const jit = <T>(init: (...args: any[]) => T): (() => T) => {
    let d: T;
    return (...args) => {
        d = d || init(...args);
        return d;
    };
};

export const alphaSort = <T>(a: T, b: T) => {
    if(a > b) return 1;
    if(a < b) return -1 
    return 0;
}